import { formatDispensingError } from './dispensingEntity';
import { isGrnPosted } from './grnEntity';
import {
  checkAvailability,
  readAvailableQuantity,
  readCanFulfill,
} from '../api/stockLedger';
import { formatQty } from '../utils';

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export function isVendorReturnGrnEligible(grn) {
  if (!grn) return false;
  return isGrnPosted(grn.status, grn.posting_status);
}

function todayIsoDate() {
  return new Date().toISOString().slice(0, 10);
}

/** Pre-submit validation — mirrors backend rules so common failures never round-trip. */
export function validateVendorReturnForm({
  grn,
  lines,
  returnDate,
  returnNumber,
  resolveItemId,
}) {
  if (!grn) {
    return { field: null, message: 'Select a posted GRN before creating a return.' };
  }
  if (!isVendorReturnGrnEligible(grn)) {
    return {
      field: null,
      message: 'This GRN is not posted to stock yet. Post the GRN first, then create a vendor return.',
    };
  }
  if (!grn.vendor_id) {
    return {
      field: null,
      message: 'This GRN has no vendor linked. Choose a GRN with a valid vendor.',
    };
  }
  if (!grn.receiving_store_id) {
    return {
      field: null,
      message: 'This GRN has no receiving store. Choose a GRN that was received into a store.',
    };
  }
  if (!returnNumber?.trim()) {
    return {
      field: null,
      message: 'Return number is missing. Refresh the page to generate a new one.',
    };
  }
  if (!returnDate) {
    return { field: 'return_date', message: 'Return date is required.' };
  }
  if (returnDate > todayIsoDate()) {
    return { field: 'return_date', message: 'Return date cannot be in the future.' };
  }
  if (!lines?.length) {
    return { field: null, message: 'This GRN has no rejected quantity to return.' };
  }

  for (const line of lines) {
    const qty = Math.floor(Number(line.return_quantity)) || 0;
    if (!line.item_id) {
      return {
        field: null,
        message: 'One or more lines are missing an item. Reload the GRN and try again.',
      };
    }
    if (!line.grn_item_id) {
      return {
        field: null,
        message: 'One or more lines are missing a GRN item reference. Reload the GRN and try again.',
      };
    }
    if (qty <= 0) {
      return {
        field: 'return_quantity',
        message: 'Each return line must have a quantity of at least 1.',
      };
    }
    if (qty > line.rejectedQty) {
      return {
        field: 'return_quantity',
        message: `Return quantity cannot exceed the rejected quantity (${formatQty(line.rejectedQty)}).`,
      };
    }
    if (line.availableQty != null && qty > line.availableQty) {
      const itemLabel = typeof resolveItemId === 'function'
        ? resolveItemId(line.item_id)
        : (line.item_name && !UUID_RE.test(line.item_name) ? line.item_name : 'this item');
      return {
        field: 'return_quantity',
        message: buildInsufficientStockMessage({
          itemName: itemLabel,
          requested: qty,
          available: line.availableQty,
          actionVerb: 'create',
        }),
      };
    }
  }

  return null;
}

export function validateVendorReturnCancel(reason) {
  if (!reason?.trim()) {
    return { field: 'reason', message: 'Please enter a reason before cancelling this vendor return.' };
  }
  return null;
}

export function buildInsufficientStockMessage({
  itemName,
  requested,
  available,
  actionVerb = 'confirm',
}) {
  return `Not enough stock for ${itemName}. This return needs ${formatQty(requested)}, but only ${formatQty(available)} is available in the store. Reduce the return quantity or check stock levels before you ${actionVerb}.`;
}

async function checkLineStockShortfall(storeId, line, resolveItemId) {
  const qty = Math.floor(Number(line.return_quantity)) || 0;
  if (!storeId || !line?.item_id || qty <= 0) return null;

  const params = {
    store_id: storeId,
    item_id: line.item_id,
    quantity: qty,
  };
  // return_quantity is in the GRN line's original uom_id (often BOX, not base) —
  // without this the ledger's to_base() gate assumes base and compares raw
  // mismatched units against on-hand (which is always base/TABLET).
  if (line.uom_id) params.uom_id = line.uom_id;
  if (line.batch_number) params.batch_number = line.batch_number;

  try {
    const avail = await checkAvailability(params);
    const onHand = readAvailableQuantity(avail);
    const canFulfill = readCanFulfill(avail);
    const insufficient = canFulfill === false
      || (onHand != null && onHand < qty);

    if (!insufficient) {
      return { available: onHand, shortfall: null };
    }

    const itemName = typeof resolveItemId === 'function'
      ? resolveItemId(line.item_id)
      : 'this item';
    const available = onHand ?? 0;

    return {
      available,
      shortfall: {
        item_id: line.item_id,
        itemName,
        requested: qty,
        available,
        message: buildInsufficientStockMessage({
          itemName,
          requested: qty,
          available,
          actionVerb: 'confirm',
        }),
      },
    };
  } catch {
    return null;
  }
}

/** Returns the first line that cannot be fulfilled from the given store, or null. */
export async function findVendorReturnStockShortfall({
  storeId,
  lines,
  resolveItemId,
  actionVerb = 'confirm',
}) {
  if (!storeId || !lines?.length) return null;

  for (const line of lines) {
    const result = await checkLineStockShortfall(storeId, line, resolveItemId);
    if (result?.shortfall) {
      return {
        ...result.shortfall,
        message: buildInsufficientStockMessage({
          itemName: result.shortfall.itemName,
          requested: result.shortfall.requested,
          available: result.shortfall.available,
          actionVerb,
        }),
      };
    }
  }
  return null;
}

/** Attach availableQty / stockShort flags for create-form line rows. */
export async function enrichVendorReturnLinesWithAvailability(storeId, lines, resolveItemId) {
  if (!storeId || !lines?.length) return lines;

  return Promise.all(lines.map(async (line) => {
    const result = await checkLineStockShortfall(storeId, line, resolveItemId);
    const availableQty = result?.available ?? null;
    const qty = Math.floor(Number(line.return_quantity)) || 0;
    const stockShort = availableQty != null && qty > availableQty;
    return { ...line, availableQty, stockShort };
  }));
}

function parseInsufficientStock(message) {
  const match = String(message || '').match(/requested\s+([\d.]+).*available\s+([\d.]+)/i);
  if (!match) return null;
  return { requested: match[1], available: match[2] };
}

function extractItemId(message) {
  const match = String(message || '').match(UUID_RE);
  return match ? match[0] : null;
}

function stripTechnicalNoise(message) {
  return String(message || '')
    .replace(/\{[\s\S]*\}/g, '')
    .replace(/request_id[^\s]*/gi, '')
    .replace(/HTTP\s+\d+:\s*/gi, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function resolveItemLabel(message, resolveItemId) {
  const itemId = extractItemId(message);
  if (itemId && typeof resolveItemId === 'function') {
    const name = resolveItemId(itemId);
    if (name && name !== '—') return name;
  }
  return 'this item';
}

function insufficientStockMessage(message, resolveItemId, actionVerb) {
  const itemName = resolveItemLabel(message, resolveItemId);
  const qty = parseInsufficientStock(message);

  if (qty) {
    return `Not enough stock for ${itemName}. This return needs ${formatQty(qty.requested)}, but only ${formatQty(qty.available)} is available in the store. Reduce the return quantity or check stock levels before you ${actionVerb}.`;
  }

  return `Not enough stock for ${itemName}. Check store stock and adjust the return quantity before you ${actionVerb}.`;
}

/**
 * Map vendor-return API errors to user-facing copy (no UUIDs, JSON blobs, or HTTP codes).
 */
export function formatVendorReturnError(err, opts = {}) {
  const {
    resolveItemId,
    action = 'save',
    fallback = 'Could not complete this vendor return. Please try again.',
  } = opts;

  const raw = formatDispensingError(err, fallback);
  const msg = raw || '';
  const lower = msg.toLowerCase();

  const actionVerb = action === 'confirm'
    ? 'confirm'
    : action === 'cancel'
      ? 'cancel'
      : action === 'load'
        ? 'load'
        : 'continue';

  if (/insufficient stock|stock deduction failed/i.test(lower)) {
    return insufficientStockMessage(msg, resolveItemId, actionVerb);
  }

  if (err?.status === 409 || /already exists|duplicate/i.test(lower)) {
    return 'This return number is already in use. Refresh the page to get a new number, then try again.';
  }

  if (/already confirmed|already cancelled|status.*not.*pending|status.*not.*draft/i.test(lower)) {
    return 'This return was already updated. The page has been refreshed — check the current status and try again if needed.';
  }

  if (/not posted|posted_to_stock|post to stock/i.test(lower)) {
    return 'The source GRN must be posted to stock before you can create or confirm a vendor return.';
  }

  if (/exceed|greater than|rejected/i.test(lower)) {
    return 'Return quantity cannot be more than the rejected quantity on that line. Adjust the quantities and try again.';
  }

  if (/no rejected|nothing to return/i.test(lower)) {
    return 'This GRN has no rejected quantity left to return.';
  }

  if (action === 'cancel' && /reason.*required/i.test(lower)) {
    return 'Please enter a reason before cancelling this vendor return.';
  }

  if (err?.status === 404) {
    if (action === 'load') {
      return 'Vendor return not found. It may have been removed — go back to the list and try again.';
    }
    return 'The selected GRN or vendor return was not found. Refresh and try again.';
  }

  if (err?.status >= 500) {
    return 'Server error — please try again in a moment.';
  }

  if (!err?.status && err?.message === 'Network Error') {
    return 'Network error — check your connection and try again.';
  }

  if (err?.status === 422) {
    const cleaned = stripTechnicalNoise(msg);
    if (cleaned && !UUID_RE.test(cleaned)) return cleaned;
    return 'Some fields are invalid. Check return quantities and try again.';
  }

  const cleaned = stripTechnicalNoise(msg);
  if (cleaned && !UUID_RE.test(cleaned)) return cleaned;
  return fallback;
}

/** Load-time errors when fetching a source GRN on the create form. */
export function formatVendorReturnLoadError(err, opts = {}) {
  return formatVendorReturnError(err, {
    ...opts,
    action: 'load',
    fallback: opts.fallback || 'Could not load the selected GRN. Search for another posted GRN and try again.',
  });
}
